import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import UserLink from '../../userLink';
import AttributeChip from '../../attributeChip';
import { NO_SCORE_PLACEHOLDER } from '../../../pages/competenceMapPage/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    boxSizing: 'border-box',
    backgroundColor: primary.bluish9,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(4),
    gap: spacing(3),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing(2),
  },
  user: {
    minWidth: 0,
    flex: 1,
  },
  matchChip: {
    backgroundColor: primary.bluish8,
    borderRadius: 8,
    color: primary.bluish2,
    flexShrink: 0,
    padding: spacing(0.5, 1.5),
  },
  attributes: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  attributeRow: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing(2),
    minWidth: 0,
  },
  attributeChip: {
    minWidth: 0,
    flex: 1,
  },
  score: {
    alignItems: 'center',
    borderRadius: 8,
    display: 'flex',
    flexShrink: 0,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  scoreWithValue: {
    color: primary.white,
  },
  scorePlaceholder: {
    backgroundColor: primary.bluish8,
    color: primary.bluish4,
  },
}));

const UserCompetenceCard = ({ matchedUser, isUserProfileAccessible }) => {
  const classes = useStyles();
  const { user, attributesWithScores, matchCount, totalAttributes } =
    matchedUser;
  const canAccessProfile = isUserProfileAccessible(user.id);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <UserLink
          className={classes.user}
          user={user}
          isUserAccessible={canAccessProfile}
          avatarProps={{
            variant: 'subtitle1',
            small: true,
            caption: true,
          }}
        />
        <Typography
          className={classes.matchChip}
          variant="subtitle2"
          aria-label={`${matchCount} of ${totalAttributes} attributes matched`}
        >
          {`${matchCount}/${totalAttributes}`}
        </Typography>
      </div>
      <div className={classes.attributes}>
        {attributesWithScores.map(attribute => {
          const hasScore =
            attribute.score !== null && attribute.score !== undefined;

          return (
            <div
              key={`user_competence_${user.id}_${attribute.id}`}
              className={classes.attributeRow}
            >
              <AttributeChip
                customClass={classes.attributeChip}
                attribute={attribute}
                name={attribute.name}
                color={attribute.color}
              />
              <Typography
                className={classNames(classes.score, {
                  [classes.scoreWithValue]: hasScore,
                  [classes.scorePlaceholder]: !hasScore,
                })}
                style={
                  hasScore ? { backgroundColor: attribute.color } : undefined
                }
                variant="overline"
              >
                {hasScore
                  ? Math.round(attribute.score * 10) / 10
                  : NO_SCORE_PLACEHOLDER}
              </Typography>
            </div>
          );
        })}
      </div>
    </div>
  );
};

UserCompetenceCard.propTypes = {
  matchedUser: PropTypes.shape({
    user: PropTypes.object.isRequired,
    attributesWithScores: PropTypes.arrayOf(PropTypes.object).isRequired,
    matchCount: PropTypes.number.isRequired,
    totalAttributes: PropTypes.number.isRequired,
  }).isRequired,
  isUserProfileAccessible: PropTypes.func.isRequired,
};

export default UserCompetenceCard;
